// ──────────────────────────────────────────────────────────────
//  Auth.ts — Client-side authentication module.
//
//  Replaces the old AbstractID.ts redirect-based auth with a
//  self-contained auth system.  This app IS the identity provider,
//  so login/register/2FA all happen via our own pages and API.
//
//  Exports:
//   - Svelte stores for auth state (`isAuthorized`, `tokenData`, etc.)
//   - `getToken()` / `setToken()` / `clearToken()` for network layer
//   - `initialize()` for app bootstrap
//   - `logout()` for sign-out
//   - `refreshTokenPeriodically()` for background token refresh
//
//  The JWT payload contains everything the UI shell needs: id,
//  firstName, lastName, email, isAdmin, roles, themeMode,
//  notifications, lang, profilePictureURL.  No extra
//  API call is required at login or init time.  (A `tokenBalance`
//  claim is also emitted, but it is a fixed '0' kept only for other
//  products that decode this JWT — AbstractID no longer does token
//  accounting, and nothing here reads it.)  Profile-heavy pages
//  (Settings) can lazily fetch the full User record via
//  `loadFullProfile()` when needed.
// ──────────────────────────────────────────────────────────────

import { writable, derived, get } from 'svelte/store';
import { jwtDecode } from 'jwt-decode';
import type { TokenData, User } from './AppTypes';
import { storable } from './storable';

// ── Constants ─────────────────────────────────────────────────

/** LocalStorage key for the JWT string. */
const TOKEN_KEY = 'auth_token';

/** LocalStorage key for the persisted color theme. */
const THEME_KEY = 'color-theme';

/** LocalStorage key for the remembered preferred sign-in method. */
const PREFERRED_AUTH_METHOD_KEY = 'preferred_auth_method';

/** Interval between automatic token refreshes (milliseconds). */
const REFRESH_INTERVAL_MS = 60_000;

// ── Stores ────────────────────────────────────────────────────

/**
 * Whether the user has a valid, non-expired JWT.
 * Components should gate protected content on this.
 */
export const isAuthorized = writable(false);

/**
 * Whether auth initialisation is still in progress.
 * While `true` the app should show a loading spinner.
 */
export const isAuthorizationPending = writable(true);

/**
 * Set to `true` by `logout()` before any store updates occur.
 * The admin shell checks this to suppress re-renders (avoids a
 * flash of signed-out UI while the browser navigates away).
 */
export const isLoggingOut = writable(false);

/**
 * The decoded JWT payload, or `null` when not authenticated.
 * Reactive — updates every time `setToken` is called.
 */
export const tokenData = writable<TokenData | null>(null);

/**
 * Full user record fetched lazily from `/api/auth/me`.
 * Contains profile fields not in the JWT (imageName, about, company,
 * twoFactorEnabled, etc.).  Only populated when a page explicitly
 * calls `loadFullProfile()` — NOT at login time.
 */
export const currentUser = writable<User | null>(null);

/**
 * Derived store — `true` when the current user is an admin.
 * Reads directly from the decoded JWT (which includes `isAdmin`).
 * Used by sidebar, route guards, and admin page visibility.
 */
export const isAdmin = derived(tokenData, ($token) => $token?.isAdmin === true);

/**
 * Reactive unread-notification count.  Initialised from the JWT payload
 * when a token is set, then kept current by a 30-second polling loop
 * which writes to this store after every fetch.
 */
export const unreadNotificationCount = writable(0);

/** Default theme based on OS preference (dark if system prefers dark, otherwise light). */
/* c8 ignore next -- environment-dependent initialization, tested via browser */
const defaultTheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';

/**
 * User's preferred color theme, persisted in localStorage via storable().
 * Defaults to OS preference on first visit.
 */
export const colorTheme = storable<string>(THEME_KEY, defaultTheme, false);

/* Apply the CSS dark class whenever the theme changes. */
colorTheme.subscribe((value) => {
	document.documentElement.classList.toggle('dark', value === 'dark');
});

/**
 * The sign-in methods the login page can pre-select on a return visit.
 * Mirrors the three interactive paths offered on the login screen.
 */
export type AuthMethod = 'email' | 'password' | 'passkey';

/**
 * The user's remembered preferred sign-in method, persisted in
 * localStorage via storable(). An empty string means "no preference
 * saved" — the login page then falls back to its default email flow.
 * Set when the user ticks "Remember my choice" (or switches method with
 * it ticked); cleared when they untick it.
 */
export const preferredAuthMethod = storable<AuthMethod | ''>(PREFERRED_AUTH_METHOD_KEY, '', false);

// ── Token management ──────────────────────────────────────────

/** Handle for the periodic refresh `setInterval`, so we can clear it. */
let refreshHandle: ReturnType<typeof setInterval> | null = null;

/** Handle for the notification-count polling interval. */
let notificationPollHandle: ReturnType<typeof setInterval> | null = null;

/** Interval between notification-count polls (milliseconds). */
const NOTIFICATION_POLL_INTERVAL_MS = 30_000;

/**
 * Fetch the unread notification count from the server and update the
 * shared `unreadNotificationCount` store.  Called on a 30-second loop
 * so the AbstractIDCard badge stays current.
 */
async function pollNotificationCount(): Promise<void> {
	try {
		const { listNotifications } = await import('./AppNetwork');
		const result = await listNotifications({ limit: 20 });
		const unread = result.items.filter((n: { isRead: boolean }) => !n.isRead).length;
		unreadNotificationCount.set(unread);
	} catch {
		/* Silently ignore — will retry next interval. */
	}
}

/**
 * Read the raw JWT string from localStorage.
 * Called by `AppNetwork.ts` to attach the `Authorization` header.
 */
export function getToken(): string | null {
	return localStorage.getItem(TOKEN_KEY);
}

/**
 * Persist a new JWT string, decode it, and update auth stores.
 *
 * If the token is expired or malformed the stores are cleared and
 * the token is removed from storage.
 *
 * Also (re)starts the background refresh timer so that tokens
 * received from login/register are continuously refreshed — not
 * just those present at `initialize()` time.
 *
 * @param jwt  - The raw JWT string received from the server.
 */
export function setToken(jwt: string): void {
	try {
		const decoded = jwtDecode<TokenData>(jwt);

		/* Reject expired tokens immediately. */
		if (decoded.exp && decoded.exp * 1000 < Date.now()) {
			clearToken();
			return;
		}

		localStorage.setItem(TOKEN_KEY, jwt);
		tokenData.set(decoded);
		isAuthorized.set(true);
		unreadNotificationCount.set(decoded.notifications?.unread ?? 0);

		/* Ensure the background refresh loop is running. */
		refreshTokenPeriodically();
	} catch {
		clearToken();
	}
}

/**
 * Remove the JWT from storage and reset all auth stores to their
 * logged-out defaults.
 */
export function clearToken(): void {
	localStorage.removeItem(TOKEN_KEY);
	tokenData.set(null);
	currentUser.set(null);
	isAuthorized.set(false);
	unreadNotificationCount.set(0);
}

// ── User loading ──────────────────────────────────────────────

/**
 * Lazily fetch the full user record from `GET /api/auth/me`.
 *
 * NOT called at login/init — the JWT has everything the shell needs.
 * Only call this from profile-heavy pages that need fields not in
 * the JWT (imageName, about, company, twoFactorEnabled, etc.).
 *
 * Uses dynamic import to avoid a circular dependency at module-
 * evaluation time (AppNetwork imports `getToken` from this file).
 */
export async function loadFullProfile(): Promise<User | null> {
	try {
		const { getAuthMe } = await import('./AppNetwork');
		const user = await getAuthMe();
		currentUser.set(user);
		return user;
	} catch {
		return null;
	}
}

// ── Lifecycle ─────────────────────────────────────────────────

/**
 * Whether `jwt` decodes and has not yet expired.  A token without
 * an `exp` claim counts as expired, matching `initialize()`.
 */
function isUnexpired(jwt: string | null): boolean {
	if (!jwt) return false;
	try {
		return (jwtDecode<TokenData>(jwt).exp ?? 0) * 1000 > Date.now();
	} catch {
		return false;
	}
}

/**
 * Adopt a session token handed over in the URL fragment (`#token=…`).
 *
 * Consumer applications send users here already signed in — the
 * licensing application does so for everyone without its admin role,
 * since licence self-service lives here now.  The token is the user's
 * own AbstractID session JWT (the one `/authorize` gave that
 * application), so there is nothing to exchange.  It travels in the
 * fragment so no server or access log ever sees it, and is stripped
 * from the address bar before anything else reads the URL.
 *
 * A valid session already held here wins: a crafted link must not be
 * able to swap the signed-in user for someone else.
 */
function adoptHandedOverToken(): void {
	const hash: string = window.location.hash ?? '';
	/* No fragment, nothing handed over. */
	if (!hash.startsWith('#')) return;

	const parameters = new URLSearchParams(hash.slice(1));
	const handedOver = parameters.get('token');
	/* A fragment that carries no token belongs to someone else. */
	if (!handedOver) return;

	/* Strip the token first, so it never lingers in the address bar or history — even when it is rejected. */
	parameters.delete('token');
	const remaining = parameters.toString();
	window.history.replaceState(
		window.history.state,
		'',
		`${window.location.pathname}${window.location.search}${remaining ? `#${remaining}` : ''}`
	);

	/* A live session here wins; an expired or malformed handover is dropped. */
	if (isUnexpired(getToken()) || !isUnexpired(handedOver)) return;
	localStorage.setItem(TOKEN_KEY, handedOver);
}

/**
 * Bootstrap the auth module on app startup.
 *
 * 0. Adopts a token handed over in the URL fragment, if any.
 * 1. Reads any existing token from localStorage.
 * 2. If valid and not expired — decodes it, starts refresh.
 * 3. If expired or missing — clears auth state (user sees login page).
 * 4. Applies the persisted color-theme to the `<html>` element.
 * 5. Marks authorisation as no longer pending.
 */
export async function initialize(): Promise<void> {
	adoptHandedOverToken();

	const existingToken = getToken();

	/* Decode the existing token, hydrate stores if valid, or clear if expired/malformed. */
	if (existingToken) {
		try {
			const decoded = jwtDecode<TokenData>(existingToken);
			const expiresAt = (decoded.exp ?? 0) * 1000;

			if (expiresAt > Date.now()) {
				/* Token is still valid — hydrate stores. */
				tokenData.set(decoded);
				isAuthorized.set(true);

				/* Start background refresh so the token stays fresh. */
				refreshTokenPeriodically();
			} else {
				/* Token expired — clean up. */
				clearToken();
			}
		} catch {
			clearToken();
		}
	}

	/* Apply persisted theme. */
	const theme = get(colorTheme);
	document.documentElement.classList.toggle('dark', theme === 'dark');

	isAuthorizationPending.set(false);
}

/**
 * Refresh the session token once, now, and return the token to use.
 *
 * A token's roles are fixed when it is minted, and the background refresh only runs while a page
 * of this app stays open. So a token handed to another application straight after a page load —
 * `/authorize` does exactly that — can be one minted days ago, carrying none of the roles granted
 * since: an administrator given a consumer app's admin role arrives without it and is turned away.
 * Refreshing first hands over the user's current roles.
 *
 * Best-effort: if the refresh fails the current token is returned unchanged, since a stale token
 * is still a valid one and the hand-off should not fail over it. A 401 has already forced a sign-out
 * inside `unwrap()`, in which case there is no token and `null` comes back.
 *
 * @returns The fresh token, else the current one, else `null`.
 */
export async function refreshTokenNow(): Promise<string | null> {
	/* Refresh, store and return the fresh token; keep the current one on failure. */
	try {
		const { refreshToken } = await import('./AppNetwork');
		const result = await refreshToken();
		if (result?.token) {
			setToken(result.token);
			return result.token;
		}
	} catch (err) {
		console.warn('[Auth] Token refresh before hand-off failed:', err);
	}
	return getToken();
}

/**
 * Start a repeating timer that refreshes the JWT in the background.
 *
 * Called automatically by `initialize()` when a valid token exists,
 * and again after every successful login/register.  Clears any
 * previous timer first to avoid duplicates.
 */
export function refreshTokenPeriodically(): void {
	stopRefresh();

	/* Periodically refresh the JWT and poll for unread notifications. */
	refreshHandle = setInterval(async () => {
		try {
			const { refreshToken } = await import('./AppNetwork');
			const result = await refreshToken();
			if (result?.token) {
				setToken(result.token);
			}
		} catch (err) {
			/* unwrap() already handles 401 (force logout) and 403 (error toast).
			   Log for diagnostics — transient failures will retry next interval. */
			console.warn('[Auth] Token refresh failed:', err);
		}
	}, REFRESH_INTERVAL_MS);

	/* Start the notification-count poll alongside the token refresh. */
	pollNotificationCount();
	notificationPollHandle = setInterval(pollNotificationCount, NOTIFICATION_POLL_INTERVAL_MS);
}

/**
 * Stop the background token-refresh timer.
 */
export function stopRefresh(): void {
	if (refreshHandle !== null) {
		clearInterval(refreshHandle);
		refreshHandle = null;
	}
	/* Clear the notification-count polling timer if active. */
	if (notificationPollHandle !== null) {
		clearInterval(notificationPollHandle);
		notificationPollHandle = null;
	}
}

/**
 * Sign the user out.
 *
 * Stops the refresh timer, clears the token, and navigates to the
 * login page via a full page load (cross-app navigation from admin
 * to the public auth app).
 */
export function logout(): void {
	/* Unmount the Svelte app so no reactive updates run — the DOM
	   stays frozen while we clear state and navigate to /login. */
	isLoggingOut.set(true);
	window.__destroyApp?.();
	stopRefresh();
	clearToken();
	window.location.href = '/login';
}

// ── Convenience helpers ───────────────────────────────────────

/**
 * Check whether the current user has admin privileges (imperative).
 * For reactive checks, use the `isAdmin` derived store instead.
 */
export function isAdminUser(): boolean {
	const token = get(tokenData);
	return token?.isAdmin === true;
}
